<script lang="ts">
import {
  iButton,
  iButtonStyles,
  iButtonTypes,
  iButtonSizes,
  iKeyIcon,
  iKeyIconShape,
  iKeyIconType,
  iKeyIconStyle,
  iKeyIconSize,
  iBadge,
  iBadgeColours,
} from '@inscribe/signal';
import { mapActions } from 'vuex';
import { ChatContext, ChatContextType, UiMessageType } from '@/types/chat';
import { getSuggestedPrompts } from '@/utils/chat';
import { Document } from '@/types/document';
import ChatPanelSuggestedPrompt from '@/V2/chat/components/ChatPanelSuggestedPrompt.vue';
import ChatPanelTip from '@/V2/chat/components/ChatPanelTip.vue';
import { mapGetters } from 'vuex';
import { PropType } from 'vue';
import { SuggestedChatPrompt } from '@/constants/chat';

export default {
  name: 'ChatPanelEmptyState',
  components: {
    iBadge,
    iButton,
    iKeyIcon,
    ChatPanelSuggestedPrompt,
    ChatPanelTip,
  },
  props: {
    currentContextType: {
      type: String as PropType<ChatContextType>,
      required: true,
    },
    contexts: {
      type: Array as PropType<ChatContext[]>,
      required: true,
    },
    document: {
      type: Object as PropType<Document>,
      default: undefined,
    },
  },
  setup() {
    return {
      iButtonStyles,
      iButtonTypes,
      iButtonSizes,
      UiMessageType,
      iKeyIconShape,
      iKeyIconType,
      iKeyIconStyle,
      iKeyIconSize,
      iBadgeColours,
    };
  },
  computed: {
    ...mapGetters([
      'currentChatTip',
      'getIsGeneratingForConversationUuid',
      'currentConversationUuid',
      'tipShouldAppear',
    ]),
    suggestedPrompts() {
      return getSuggestedPrompts({
        chatContextType: this.currentContextType,
        document: this.document,
      });
    },
    isGenerating() {
      return this.getIsGeneratingForConversationUuid(
        this.currentConversationUuid,
      );
    },
    tipIsVisible() {
      return this.tipShouldAppear && this.currentChatTip && !this.isGenerating;
    },
  },
  methods: {
    ...mapActions(['submitConversationMessage', 'createNewConversationUuid']),
    onSuggestedPromptClicked(prompt: SuggestedChatPrompt) {
      const customerContext = this.contexts.find(
        (context) => context.type === ChatContextType.Customer,
      );
      const documentContext = this.contexts.find(
        (context) => context.type === ChatContextType.Document,
      );
      const contextCustomerUuids = customerContext
        ? [customerContext.customerUuid]
        : [];
      const contextDocumentIds = documentContext
        ? [documentContext.documentId]
        : [];
      this.createNewConversationUuid();
      this.submitConversationMessage({
        conversationUuid: this.currentConversationUuid,
        userMessageContent: prompt.expandedPrompt ?? prompt.prompt,
        contextCustomerUuids,
        contextDocumentIds,
      });
    },
  },
};
</script>

<template>
  <div class="chat-empty-state d-flex flex-column i-gap-8">
    <!-- empty state message -->
    <div
      class="empty-state-message d-flex flex-column align-items-center justify-content-center"
    >
      <i-key-icon
        class="i-mb-6"
        icon="medium-ai-scan"
        :shape="iKeyIconShape.SQUARE"
        :type="iKeyIconType.NEUTRAL"
        :size="iKeyIconSize.LARGE"
        :icon-style="iKeyIconStyle.OUTLINE"
      />
      <div class="title i-text-button-2">Chat with your Assistant</div>
      <div class="subtitle i-text-body-2 i-mb-3">
        Investigate fraud across customers and documents
      </div>
      <i-badge
        :colour="iBadgeColours.NEUTRAL"
        label="BETA"
        :outline="true"
      ></i-badge>
    </div>

    <div class="d-flex flex-column align-items-start i-gap-4">
      <i-button
        v-for="prompt in suggestedPrompts"
        :key="prompt.prompt"
        class="d-flex flex-grow-1"
        :type="iButtonTypes.NEUTRAL"
        :button-style="iButtonStyles.SOLID_LIGHT"
        :size="iButtonSizes.SMALL"
        :label="prompt.prompt"
        :aria-label="`Assistant: Suggested prompt '${prompt.prompt}'`"
        data-dd-action-name="chat-panel-suggested-prompt"
        @button-click="onSuggestedPromptClicked(prompt)"
      ></i-button>
    </div>

    <chat-panel-tip v-if="tipIsVisible" :tip="currentChatTip" />
  </div>
</template>

<style lang="scss" scoped>
.chat-empty-state {
  height: 100%;

  .title {
    color: var(--text-neutral-bold);
    position: relative;

    .beta-badge {
      color: var(--text-neutral-subtle);
      font-size: 10px;
      position: absolute;
      bottom: 2px;
      left: calc(100% + 6px);
    }
  }

  .subtitle {
    color: var(--text-neutral);
  }

  .empty-state-message {
    flex: 1;
  }

  .default-prompts {
    $radius: 20px;

    .chat-panel-suggested-prompt {
      &:first-of-type {
        border-top-left-radius: $radius;
        border-top-right-radius: $radius;
      }
      &:last-of-type {
        border-bottom-left-radius: $radius;
        border-bottom-right-radius: $radius;
      }
    }
  }
}
</style>
